import { GetPromotionChargeBaseRes, GetServiceTypeListRes, PostPromotionListReq, UnitType } from '@/services/settlement/settlementPromotion/types'
import { Utils } from '@/utils/Utils'

/**
 * LP8658 박스단위 입고 정산 — 프로모션 "서비스 항목" unit_type(PCS/BOX) 분화 유틸.
 *
 * BE `/service-types` 는 INBOUND 을 단일 항목(`{ code: 'INBOUND', name: '입고비' }`)으로 내려준다.
 * 화면은 INBOUND 만 PCS/BOX 2개 항목으로 분화해 노출하고, 선택값을 `serviceType` + `unitType` 으로 분해한다(§3.2).
 * 라벨 unit 표기는 정산 상세/Excel 과 정합하도록 대문자(PCS/BOX) 로 통일(2026-06-02 결정).
 * UnitType 정의는 types.ts 단일 출처를 import (중복 정의 방지).
 */

// unit 분화 대상 serviceType (PRD: 입고비만 박스단위). 확장 대비 상수.
export const UNIT_TYPED_SERVICE_TYPES = ['INBOUND'] as const

// LP8658 박스단위 입고 정산 feature flag 키. Modal/Form/Table 3곳이 동일 키를 참조하도록 단일 출처화 (오타 시 분기 불일치 방지)
export const INBOUND_SETTLEMENT_UNIT_TYPE_FLAG = 'feature.inboundSettlementUnitTypeEnabled' as const

const UNIT_TYPES: UnitType[] = ['PCS', 'BOX']
const UNIT_TYPE_SET = new Set<string>(UNIT_TYPES)
const CHARGE_ITEM_DELIMITER = '|'

export const isUnitTyped = (code: string): boolean => (UNIT_TYPED_SERVICE_TYPES as readonly string[]).includes(code)

/** (serviceType, unitType) → composite SelectBox value */
export const toChargeItemValue = (serviceType: string, unitType: UnitType = 'PCS'): string =>
  `${serviceType}${CHARGE_ITEM_DELIMITER}${unitType}`

/** composite value → { serviceType, unitType }. 알 수 없는/누락 unit 토큰은 PCS 로 폴백 (UNIT_TYPES 화이트리스트 — 향후 타입 확장 대비) */
export const parseChargeItemValue = (value: string): { serviceType: string; unitType: UnitType } => {
  const [serviceType, unitType] = value.split(CHARGE_ITEM_DELIMITER)
  return { serviceType, unitType: unitType && UNIT_TYPE_SET.has(unitType) ? (unitType as UnitType) : 'PCS' }
}

/**
 * 시안 v4 명칭 — BE `/service-types` 의 name 과 다른 항목만 화면에서 바꿔 부른다.
 * 작업비와 택배비를 짝으로 읽히게 하려는 것 (고객출고비 → 고객출고 작업비).
 */
const MOCKUP_SERVICE_TYPE_NAMES: Record<string, string> = {
  OUTBOUND_TO_CUSTOMER: '고객출고 작업비',
  OUTBOUND_TO_STORE: '매장출고 작업비',
  RETURN_INBOUND: '고객반품 작업비',
  RETURN_INBOUND_SHIPPING: '고객반품 택배비',
}

/** 서비스 항목 라벨: INBOUND → "입고비 PCS단가"/"입고비 BOX단가", 그 외 → 시안 명칭(없으면 serviceTypeName) */
export const formatChargeItemLabel = (code: string, serviceTypeName: string, unitType: UnitType = 'PCS'): string => {
  const name = MOCKUP_SERVICE_TYPE_NAMES[code] ?? serviceTypeName
  return isUnitTyped(code) ? `${name} ${unitType}단가` : name
}

/** 서비스 항목 SelectBox 옵션: INBOUND 2건(PCS/BOX), 그 외 1건. value=`${code}|${unitType}` */
export const buildChargeItemOptions = (serviceTypeList: GetServiceTypeListRes[]): { value: string; label: string }[] =>
  serviceTypeList.flatMap((item) =>
    isUnitTyped(item.code)
      ? UNIT_TYPES.map((unitType) => ({
          value: toChargeItemValue(item.code, unitType),
          label: formatChargeItemLabel(item.code, item.name, unitType),
        }))
      : [{ value: toChargeItemValue(item.code), label: formatChargeItemLabel(item.code, item.name) }],
  )

/**
 * LP8658 — 프로모션 목록 검색 submitParams 정규화 (순수 함수, 단위 테스트 대상).
 * - serviceType: 단일/빈배열을 배열로 정규화하고 빈 배열은 null
 * - unitType: 검색 serviceType 에 INBOUND(unit 분화 대상)가 포함될 때만 유지, 비-INBOUND/전체는 null (FR-PR08 / FR-PR09)
 * - status: '' → null
 */
export const buildPromotionListSubmitParams = (
  params: ApiPaginationReq<PostPromotionListReq>,
): ApiPaginationReq<PostPromotionListReq> => {
  const normalizedServiceType = Utils.emptyArrayToNull(Utils.normalizeToArray(params.serviceType))
  const isUnitTypedSearch = !!normalizedServiceType?.some((code) => isUnitTyped(code))
  return {
    ...params,
    status: params.status === '' ? null : params.status,
    serviceType: normalizedServiceType,
    unitType: isUnitTypedSearch ? params.unitType ?? null : null,
  }
}

/**
 * LOGISPRD-11315 — 프로모션 등록 모달의 국가 · 주문유형 · 서비스 항목 옵션.
 *
 * 세 축은 `service_charge_base` 한 행(anchor)에서 함께 나온다. 조합 조회(`GET /v1/promotions/charge-bases`)
 * 결과로 앞 축이 뒤 축을 좁히므로 anchor 가 없는 조합(일본 + 국내 등)은 목록에 뜨지 않는다.
 * 조합이 비면(미배포 · 조회 실패) 구 매핑으로 폴백해 등록 자체가 막히지 않게 한다.
 */
export interface PromotionAxisOptionsInput {
  chargeBases: GetPromotionChargeBaseRes[]
  /** 폴백 전용 — 조합이 비었을 때 쓰는 서비스 항목 원천 (`GET /service-types`) */
  serviceTypeList: GetServiceTypeListRes[]
  /** 프로모션으로 등록할 수 없는 서비스 항목 (도서산간비 · 반출비 · 기타조정비) */
  excludedServiceTypes: readonly string[]
  /** LP8658 박스단위 입고 정산 flag. off 면 BOX anchor 를 빼 레거시와 같은 단일 항목만 남긴다 */
  isUnitTypeEnabled: boolean
  country?: string
  orderType?: string
}

export interface PromotionAxisOptions {
  hasChargeBases: boolean
  /** 서비스 항목 값이 `${serviceType}|${unitType}` 합성값인지 — 조합에는 단위가 항상 붙어 있다 */
  isCompositeServiceValue: boolean
  countryOptions: { value: string; label: string }[]
  orderTypeOptions: { value: string; label: string }[]
  serviceItemOptions: { value: string; label: string }[]
}

/** 조합 조회 미배포 구간 폴백 — BE 가 국가만 받던 시절의 하드코딩 매핑과 같은 값 */
const FALLBACK_COUNTRY_OPTIONS = [
  { value: 'KR', label: '한국' },
  { value: 'JP', label: '일본' },
]
const FALLBACK_ORDER_TYPE_BY_COUNTRY: Record<string, { value: string; label: string }> = {
  KR: { value: 'DOMESTIC', label: '국내' },
  JP: { value: 'GLOBAL', label: '해외' },
}

const uniqueByValue = (options: { value: string; label: string }[]) => [...new Map(options.map((opt) => [opt.value, opt])).values()]

export const buildPromotionAxisOptions = ({
  chargeBases,
  serviceTypeList,
  excludedServiceTypes,
  isUnitTypeEnabled,
  country,
  orderType,
}: PromotionAxisOptionsInput): PromotionAxisOptions => {
  // BE 도 프로모션이 성립하는 항목만 내리지만(ServiceType.PROMOTION_TYPES), 검색 폼과 같은 목록을 쓰도록 화면에서도 거른다
  const anchors = chargeBases.filter(
    (item) => !excludedServiceTypes.includes(item.serviceType.code) && (isUnitTypeEnabled || item.unitType === 'PCS'),
  )
  const hasChargeBases = anchors.length > 0
  const selectableServiceTypes = serviceTypeList.filter((item) => !excludedServiceTypes.includes(item.code))

  if (!hasChargeBases) {
    return {
      hasChargeBases,
      isCompositeServiceValue: isUnitTypeEnabled,
      countryOptions: FALLBACK_COUNTRY_OPTIONS,
      orderTypeOptions: country ? [FALLBACK_ORDER_TYPE_BY_COUNTRY[country]].filter(Boolean) : [],
      serviceItemOptions: isUnitTypeEnabled
        ? buildChargeItemOptions(selectableServiceTypes)
        : selectableServiceTypes.map((item) => ({ value: item.code, label: item.name })),
    }
  }

  return {
    hasChargeBases,
    isCompositeServiceValue: true,
    countryOptions: uniqueByValue(anchors.map((item) => ({ value: item.country.code, label: item.country.name }))),
    orderTypeOptions: country
      ? uniqueByValue(
          anchors.filter((item) => item.country.code === country).map((item) => ({ value: item.orderType.code, label: item.orderType.name })),
        )
      : [],
    serviceItemOptions:
      country && orderType
        ? uniqueByValue(
            anchors
              .filter((item) => item.country.code === country && item.orderType.code === orderType)
              .map((item) => ({
                // 값은 anchor 를 특정해야 해 단위까지 싣고, 라벨의 단위 표기는 LP8658 flag 를 따른다 (off 면 기존과 같은 항목명)
                value: toChargeItemValue(item.serviceType.code, item.unitType),
                label: isUnitTypeEnabled ? formatChargeItemLabel(item.serviceType.code, item.serviceType.name, item.unitType) : item.serviceType.name,
              })),
          )
        : [],
  }
}
